// src/components/ModalNovoAgendamento.jsx
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Save, Search } from "lucide-react";
import moment from "moment";
import "moment/locale/pt-br";

moment.locale("pt-br");

const ModalNovoAgendamento = ({
  isOpen,
  onClose,
  onSave,
  clientes = [],
  selectedSlot,      // { start: Date } opcional
  agendamentoEdit,   // objeto com dados para edição
}) => {
  const [form, setForm] = useState({
    clienteId: "",
    clienteNome: "",
    clienteTelefone: "",
    data: "",
    hora: "",
    motivo: "",
    status: "Agendado",
    duracaoMin: 60,
  });

  const [buscaCliente, setBuscaCliente] = useState("");
  const [resultadosBusca, setResultadosBusca] = useState([]);

  // Preenche ao abrir (novo/edição)
  useEffect(() => {
    if (!isOpen) return;

    if (agendamentoEdit) {
      const start = agendamentoEdit?.data?.toDate
        ? agendamentoEdit.data.toDate()
        : new Date();
      setForm({
        clienteId: agendamentoEdit.clienteId || "",
        clienteNome: agendamentoEdit.clienteNome || "",
        clienteTelefone: agendamentoEdit.clienteTelefone || "",
        data: moment(start).format("YYYY-MM-DD"),
        hora: moment(start).format("HH:mm"),
        motivo: agendamentoEdit.motivo || "",
        status: agendamentoEdit.status || "Agendado",
        duracaoMin: Number(agendamentoEdit.duracaoMin || 60),
      });
    } else {
      const dataInicial = selectedSlot
        ? moment(selectedSlot.start).format("YYYY-MM-DD")
        : "";
      const horaInicial = selectedSlot
        ? moment(selectedSlot.start).format("HH:mm")
        : "";
      setForm({
        clienteId: "",
        clienteNome: "",
        clienteTelefone: "",
        data: dataInicial,
        hora: horaInicial,
        motivo: "",
        status: "Agendado",
        duracaoMin: 60,
      });
    }

    setBuscaCliente("");
    setResultadosBusca([]);
  }, [isOpen, selectedSlot, agendamentoEdit]);

  // Busca cliente por nome (2+ letras)
  useEffect(() => {
    const termo = (buscaCliente || "").trim().toLowerCase();
    if (termo.length < 2) return setResultadosBusca([]);
    const filtrados = clientes.filter((c) =>
      (c?.nome || "").toLowerCase().includes(termo)
    );
    setResultadosBusca(filtrados.slice(0, 20));
  }, [buscaCliente, clientes]);

  if (!isOpen) return null;

  const handleSelectCliente = (c) => {
    setForm((prev) => ({
      ...prev,
      clienteId: c.id,
      clienteNome: c.nome,
      clienteTelefone: c.telefone || "",
    }));
    setBuscaCliente("");
    setResultadosBusca([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!form.clienteNome || !form.data || !form.hora || !form.motivo?.trim()) {
      Swal.fire(
        "Atenção",
        'Preencha todos os campos obrigatórios (incluindo "Motivo da Visita").',
        "warning"
      );
      return;
    }
    onSave(form);
  };

  const closeOnBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              {agendamentoEdit ? "Editar Agendamento" : "Novo Agendamento"}
            </h2>
          </div>

          {/* Body (rolável) */}
          <div className="px-7 pb-2 space-y-5 overflow-y-auto flex-1">
            {!agendamentoEdit && selectedSlot && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/40 rounded-xl text-center">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Agendando para{" "}
                  <span className="font-bold">
                    {moment(selectedSlot.start).format("dddd, D [de] MMMM")}
                  </span>{" "}
                  às{" "}
                  <span className="font-bold">
                    {moment(selectedSlot.start).format("HH:mm")}
                  </span>
                </p>
              </div>
            )}

            {/* Busca de cliente */}
            <div className="relative">
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                Buscar Cliente
              </label>
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={buscaCliente}
                  onChange={(e) => setBuscaCliente(e.target.value)}
                  placeholder="Digite para buscar cliente cadastrado…"
                  className="w-full pl-10 pr-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                />
              </div>

              {resultadosBusca.length > 0 && (
                <ul className="absolute z-50 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl mt-1 max-h-56 overflow-y-auto shadow-lg">
                  {resultadosBusca.map((c) => (
                    <li
                      key={c.id}
                      className="px-4 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 cursor-pointer rounded-lg"
                      onClick={() => handleSelectCliente(c)}
                    >
                      <p className="font-semibold text-gray-800 dark:text-gray-100">
                        {c.nome}
                      </p>
                      {c.telefone && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {c.telefone}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <InputField
              label="Nome do Cliente"
              name="clienteNome"
              value={form.clienteNome}
              onChange={handleChange}
              required
              disabled={!!form.clienteId}
            />

            <InputField
              label="Telefone (opcional)"
              name="clienteTelefone"
              value={form.clienteTelefone}
              onChange={handleChange}
              disabled={!!form.clienteId}
            />

            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Data"
                name="data"
                type="date"
                value={form.data}
                onChange={handleChange}
                required
              />
              <InputField
                label="Hora"
                name="hora"
                type="time"
                value={form.hora}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                Motivo da Visita <span className="text-red-500">*</span>
              </label>
              <textarea
                name="motivo"
                value={form.motivo}
                onChange={handleChange}
                rows={3}
                className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                placeholder="Ex: Orçamento para troca de correia dentada"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                  Status
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                >
                  <option>Agendado</option>
                  <option>Confirmado</option>
                  <option>Concluído</option>
                </select>
              </div>

              <InputField
                label="Duração (min)"
                name="duracaoMin"
                type="number"
                min="15"
                step="15"
                value={form.duracaoMin}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Botão principal (único) */}
          <div className="px-7 pb-6">
            <button
              onClick={handleSave}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              type="button"
            >
              <Save size={18} />{" "}
              {agendamentoEdit ? "Salvar Alterações" : "Salvar Agendamento"}
            </button>
          </div>
        </div>

        {/* animações iguais ao ModalNovaOS */}
        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default ModalNovoAgendamento;

/* ---------- auxiliares ---------- */
const InputField = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition disabled:bg-gray-100 dark:disabled:bg-gray-800/60"
    />
  </div>
);
